import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Search, X, SlidersHorizontal, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ChevronDown } from 'lucide-react';
import { PRODUCTS as MOCK_PRODUCTS, CATEGORIES as MOCK_CATEGORIES } from '../mock/products';
import ProductCard from './ProductCard';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE_OPTIONS = [100, 200, 500];

const SORT_OPTIONS = [
  { id: 'featured',   label: 'Featured' },
  { id: 'newest',     label: 'Newest first' },
  { id: 'price_asc',  label: 'Price: low to high' },
  { id: 'price_desc', label: 'Price: high to low' },
  { id: 'alpha_asc',  label: 'Name A → Z' },
  { id: 'alpha_desc', label: 'Name Z → A' },
];

const ProductGrid = ({ onView }) => {
  const { search: query, setSearch: setQuery, activeCat: active, setActiveCat: setActive, searchNonce } = useApp();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sort, setSort] = useState('featured');
  const [products, setProducts] = useState(MOCK_PRODUCTS);
  const [categories, setCategories] = useState(MOCK_CATEGORIES);
  const [loading, setLoading] = useState(true);
  // Collapsed by default; user preference persisted in localStorage
  const [catsOpen, setCatsOpen] = useState(() => {
    try { return localStorage.getItem('bg_cats_open') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('bg_cats_open', catsOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [catsOpen]);
  const gridTopRef = useRef(null);
  useEffect(() => {
    if (!searchNonce) return;
    const t = setTimeout(() => gridTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    return () => clearTimeout(t);
  }, [searchNonce]);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/products`, { params: { _t: Date.now() } }),
        axios.get(`${API}/categories`, { params: { _t: Date.now() } }),
      ]);
      if (Array.isArray(pRes.data) && pRes.data.length > 0) setProducts(pRes.data);
      if (Array.isArray(cRes.data) && cRes.data.length > 0) setCategories(cRes.data);
    } catch {
      // fall back to whatever we already have (mock or previous fetch)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Refetch when the tab regains focus so admin-added products go live
  // without users needing to hard-refresh.
  useEffect(() => {
    const onFocus = () => fetchAll();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchAll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchAll]);

  const filtered = useMemo(() => {
    let list = products;
    if (active !== 'all' && active !== 'top') list = list.filter(p => p.cat === active);
    if (active === 'top') list = list.filter(p => p.isTop || p.badge === 'bestseller' || p.badge_auto === 'bestseller');
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.catLabel || '').toLowerCase().includes(q));
    }
    // Sort a shallow copy so we don't mutate the source array.
    const sorted = list.slice();
    switch (sort) {
      case 'newest':      sorted.sort((a, b) => (b.id || 0) - (a.id || 0)); break;
      case 'price_asc':   sorted.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case 'price_desc':  sorted.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      case 'alpha_asc':   sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      case 'alpha_desc':  sorted.sort((a, b) => (b.name || '').localeCompare(a.name || '')); break;
      // 'featured' keeps original insertion order (product id ascending from API)
      default: break;
    }
    return sorted;
  }, [products, active, query, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, filtered.length);
  const shown = filtered.slice(startIdx, endIdx);

  // Reset to page 1 when filter/search/pageSize/sort changes
  useEffect(() => { setPage(1); }, [active, query, pageSize, sort]);

  const scrollToTop = () => {
    if (gridTopRef.current) {
      gridTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const goto = (p) => {
    const np = Math.min(totalPages, Math.max(1, p));
    setPage(np);
    scrollToTop();
  };

  // Build a compact page-number list: 1, …, 4, 5, 6, …, N
  const pageNumbers = useMemo(() => {
    const nums = [];
    const push = (n) => { if (!nums.includes(n)) nums.push(n); };
    push(1);
    for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
      if (i > 1 && i < totalPages) push(i);
    }
    if (totalPages > 1) push(totalPages);
    // Insert ellipsis markers
    const out = [];
    for (let i = 0; i < nums.length; i += 1) {
      out.push(nums[i]);
      if (i < nums.length - 1 && nums[i + 1] - nums[i] > 1) out.push('...');
    }
    return out;
  }, [currentPage, totalPages]);

  return (
    <section className="max-w-7xl mx-auto px-6 pt-14 pb-16">
      <div ref={gridTopRef} id="catalog" style={{ scrollMarginTop: 'calc(var(--header-h, 140px) + 12px)' }} className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-[38px] leading-none text-neutral-900 dark:text-neutral-100 tracking-tight">Pharmacy Catalog</h2>
          <span className="inline-flex items-center gap-1.5 bg-brand-soft text-brand text-[11.5px] px-3 py-1 rounded-full font-semibold">Bulk Pricing</span>
        </div>
        {query && (
          <button data-testid="search-results-chip" onClick={() => setQuery('')} className="inline-flex items-center gap-1.5 rounded-full border border-brand/40 bg-brand-soft text-brand px-3 py-1 text-[12.5px] font-medium">
            <Search size={12} /> Results for “{query}” <X size={12} />
          </button>
        )}
        <div className="text-[13px] text-neutral-500" data-testid="product-count">
          <span className="text-neutral-900 dark:text-neutral-100 font-medium">{filtered.length}</span> of {products.length} products
        </div>
      </div>

      {/* Category pills — collapsible + sticky */}
      <div className="sticky top-[var(--header-h,68px)] z-20 -mx-6 px-6 py-3 mb-8 bg-glass border-y">
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="categories-toggle"
            onClick={() => setCatsOpen(o => !o)}
            aria-expanded={catsOpen}
            aria-controls="category-pill-list"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] transition-all ${catsOpen ? 'bg-brand text-white border-brand shadow-sm' : 'bg-white dark:bg-[#141416] text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800 hover:border-brand hover:text-brand'}`}
          >
            <SlidersHorizontal size={13} />
            <span>Categories</span>
            <span className={`text-[11px] ${catsOpen ? 'text-white/80' : 'text-neutral-400'}`}>{categories.length}</span>
            <ChevronDown size={12} className={`transition-transform ${catsOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* When collapsed we still surface the active category as a pill so users see current filter */}
          {!catsOpen && active !== 'all' && (() => {
            const c = categories.find(cc => cc.id === active);
            if (!c) return null;
            return (
              <button
                key={c.id}
                onClick={()=>setActive('all')}
                data-testid={`cat-pill-${c.id}`}
                title="Clear filter"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] bg-brand text-white border-brand shadow-sm"
              >
                <span>{c.label}</span>
                <span className="text-[11px] text-white/80">{c.count}</span>
                <span className="text-white/80">×</span>
              </button>
            );
          })()}
        </div>

        {catsOpen && (
          <div id="category-pill-list" data-testid="category-pill-list" className="flex flex-wrap gap-2 mt-3 animate-in fade-in slide-in-from-top-1 duration-150">
            {categories.map(c => (
              <button
                key={c.id}
                onClick={()=>setActive(c.id)}
                data-testid={`cat-pill-${c.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] transition-all ${active === c.id ? 'bg-brand text-white border-brand shadow-sm' : 'bg-white dark:bg-[#141416] text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800 hover:border-brand hover:text-brand'}`}
              >
                <span>{c.label}</span>
                <span className={`text-[11px] ${active === c.id ? 'text-white/80' : 'text-neutral-400'}`}>{c.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Category title + sort + per-page selector */}
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <h3 className="font-serif text-[28px] text-neutral-900 dark:text-neutral-100">{categories.find(c => c.id === active)?.label}</h3>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-[11.5px] tracking-widest text-neutral-500 uppercase" data-testid="pagination-range">
            {filtered.length > 0 ? `${startIdx + 1}–${endIdx} of ${filtered.length}` : '0 of 0'}
          </span>
          <div className="flex items-center gap-1.5 text-[12px] text-neutral-500">
            <ArrowUpDown size={12} className="text-neutral-400" />
            <label htmlFor="sort-select" className="sr-only">Sort by</label>
            <select
              id="sort-select"
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-white dark:bg-[#141416] border border-neutral-200 dark:border-neutral-800 rounded-md px-2 py-1 text-[12.5px] text-neutral-800 dark:text-neutral-200"
            >
              {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5 text-[12px] text-neutral-500">
            <label htmlFor="page-size">Per page</label>
            <select
              id="page-size"
              data-testid="page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              className="bg-white dark:bg-[#141416] border border-neutral-200 dark:border-neutral-800 rounded-md px-2 py-1 text-[12.5px] text-neutral-800 dark:text-neutral-200"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && shown.length === 0 && (
        <div className="py-24 text-center text-neutral-500 text-[13.5px]">Loading products…</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="py-24 text-center text-neutral-500 text-[13.5px]">No products match your search.</div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {shown.map(p => <ProductCard key={p.id} product={p} onView={onView} />)}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <nav
          aria-label="Product pagination"
          data-testid="product-pagination"
          className="flex flex-wrap items-center justify-center gap-1.5 mt-10"
        >
          <button
            data-testid="page-first"
            onClick={() => goto(1)}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-1 border border-neutral-200 dark:border-neutral-700 rounded-md px-2.5 py-1.5 text-[12.5px] text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="First page"
          >
            <ChevronsLeft size={13} />
          </button>
          <button
            data-testid="page-prev"
            onClick={() => goto(currentPage - 1)}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-1 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[12.5px] text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={13} /> Prev
          </button>
          {pageNumbers.map((n, i) => n === '...' ? (
            <span key={`e${i}`} className="px-2 text-[12.5px] text-neutral-400">…</span>
          ) : (
            <button
              key={n}
              data-testid={`page-num-${n}`}
              onClick={() => goto(n)}
              aria-current={n === currentPage ? 'page' : undefined}
              className={`min-w-[34px] px-2.5 py-1.5 rounded-md text-[12.5px] border transition-all ${n === currentPage ? 'bg-brand text-white border-brand shadow-sm' : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-brand hover:text-brand'}`}
            >
              {n}
            </button>
          ))}
          <button
            data-testid="page-next"
            onClick={() => goto(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-1 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[12.5px] text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight size={13} />
          </button>
          <button
            data-testid="page-last"
            onClick={() => goto(totalPages)}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-1 border border-neutral-200 dark:border-neutral-700 rounded-md px-2.5 py-1.5 text-[12.5px] text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Last page"
          >
            <ChevronsRight size={13} />
          </button>
        </nav>
      )}

      <div className="border-t border-neutral-200 dark:border-neutral-800 mt-14 pt-6">
        <p className="text-[12.5px] text-neutral-500 leading-relaxed max-w-3xl">
          Product descriptions are general education, not diagnosis, prescribing, or personal medical advice. Prescription medicines should be used only with guidance from a licensed clinician or pharmacist.
        </p>
      </div>
    </section>
  );
};

export default ProductGrid;
